import i18n from '@/i18n';
import { getCertificationLink } from '@/services/enterpriseCertification';
import { IEnterpriseCertification } from '@/services/enterpriseCertification/interface';
import { Button, Icon, Modal, Spacing } from '@bluevision/components';
import LoadingDots from '../LoadingDots';
import * as bvfd from '@bluevision/components/foundations/variables';
import { openWindowInSafe } from '@bluevision/tools';

type CertificationType = IEnterpriseCertification.Common.CertificationType;

// 获取企业认证链接并打开
const handleCertification = async ({
  certificationType,
}: {
  certificationType?: CertificationType;
}) => {
  if (certificationType) {
    const [linkRes] = await getCertificationLink({
      certification_type: certificationType,
      redirect_url: window.location.href,
    });

    if (linkRes?.short_link) {
      openWindowInSafe(linkRes.short_link, '_self');
    }
  }
};

// 校验: 未完成认证
const handleUnCertified = ({ certificationType }: { certificationType?: CertificationType }) => {
  Modal.open({
    type: 'error',
    title: i18n.t('incomplete_certification'),
    children: i18n.t(
      'var_incomplete_certification_recharge_is_possible_only_after_completing_real_name_authentication',
      {
        var:
          certificationType === IEnterpriseCertification.Common.CertificationType.ENTERPRISE
            ? i18n.t('enterprise_authentication')
            : i18n.t('real_name_authentication_simple'),
      },
    ),
    maskClosable: false,
    centered: true,
    okText: i18n.t('go_to_identification'),
    notCloseOnOk: true,
    onOk: () => handleCertification({ certificationType }),
  });
};

// 校验: 认证进行中
const handleIngCertify = ({
  certificationType,
  pendingCertificationUrl,
}: {
  certificationType?: CertificationType;
  pendingCertificationUrl?: string;
}) => {
  Modal.open({
    type: 'warning',
    title: (
      <Spacing>
        <Icon block name='clock' blockColor={bvfd.blue70} blockFontColor={bvfd.blue10} />
        <div>
          {i18n.t('waiting_for_authentication')} <LoadingDots />
        </div>
      </Spacing>
    ),
    children: i18n.t(
      'it_takes_some_time_for_the_authentication_result_to_synchronize_please_wait_patiently',
    ),
    notCloseOnOk: true,
    centered: true,
    // 点击 x 后会调用 onCancel, 所以自定义 footer, 阻止调用 onCancel
    footer: (
      <Spacing justify='end'>
        <Button size='large' onClick={() => handleCertification({ certificationType })}>
          {i18n.t('re_initiate')}
        </Button>
        <Button
          size='large'
          type='primary'
          onClick={() => {
            if (pendingCertificationUrl) openWindowInSafe(pendingCertificationUrl, '_blank');
          }}
        >
          {i18n.t('go_to_view')}
        </Button>
      </Spacing>
    ),
  });
};

// 结果: 认证成功
const handleSuccessCertify = () => {
  Modal.open({
    title: i18n.t('certification_success'),
    type: 'success',
    children: i18n.t('certification_success'),
    hideCancel: true,
    centered: true,
    maskClosable: false,
  });
};

// 结果: 认证失败
const handleFailCertify = ({
  certificationType,
  failReason,
}: {
  certificationType?: CertificationType;
  failReason?: string;
}) => {
  Modal.open({
    title: i18n.t('certification_failure'),
    type: 'error',
    children: failReason,
    centered: true,
    maskClosable: false,
    okText: i18n.t('re_certification'),
    notCloseOnOk: true,
    onOk: () => handleCertification({ certificationType }),
  });
};

// 登录后/刷新页面 未认证提示
const handleLoginNotCertify = ({
  certificationType,
}: {
  certificationType?: CertificationType;
}) => {
  Modal.open({
    type: 'error',
    title: i18n.t('important_tips'),
    children: i18n.t('var_to_ensure_the_security_please_complete_authentication', {
      var:
        certificationType === IEnterpriseCertification.Common.CertificationType.ENTERPRISE
          ? i18n.t('enterprise_authentication')
          : i18n.t('real_name_authentication_simple'),
    }),
    maskClosable: false,
    centered: true,
    okText: i18n.t('go_to_identification'),
    notCloseOnOk: true,
    onOk: () => handleCertification({ certificationType }),
  });
};

export {
  handleUnCertified,
  handleIngCertify,
  handleSuccessCertify,
  handleFailCertify,
  handleLoginNotCertify,
};
